import { Component, OnDestroy, OnInit } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { CreatorLibraryService, creatorFailure, ItemSummary } from '../../../services/creator-library.service';

/**
 * Creator › Bibliothek › „In Wissensbasis…": save a version of the saved
 * working state, put it into the chosen knowledge base (a job) and follow the
 * job — about every 1.5 s, at most two minutes — after the dialog has closed.
 * Solutions only on request (M-h, default off). Same flow as canvas
 * `KbPublishDialog`.
 */
@Component({
  selector: 'app-creator-kb-dialog',
  templateUrl: './creator-kb-dialog.component.html',
  styleUrl: './creator-kb-dialog.component.scss',
})
export class CreatorKbDialogComponent implements OnInit, OnDestroy {
  item!: ItemSummary;
  onChanged: () => void = () => undefined;

  buckets: { id: string; name: string }[] | null = null;
  bucketId = '';
  includeSolutions = false;
  working = false;
  private destroyed = false;

  constructor(
    private ref: NbDialogRef<CreatorKbDialogComponent>,
    private library: CreatorLibraryService,
    private toastr: NbToastrService,
    private t: TranslateService,
  ) {}

  ngOnInit(): void {
    this.library.buckets().subscribe({
      next: (buckets) => (this.buckets = buckets),
      error: () => {
        this.buckets = [];
        this.toastr.danger(this.t.instant('pages.creator.kb.loadBucketsFailed'), '');
      },
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  cancel(): void {
    if (!this.working) this.ref.close();
  }

  async submit(): Promise<void> {
    if (!this.bucketId || this.working) return;
    this.working = true;
    const item = this.item;
    try {
      const versionNo = await firstValueFrom(this.library.saveVersion(item.id));
      const jobId = await firstValueFrom(this.library.publishToKb(item.id, this.bucketId, versionNo, this.includeSolutions));
      this.onChanged(); // "pending" shows up at once
      this.ref.close();
      const outcome = await this.waitForJob(jobId);
      if (outcome === 'done') {
        this.toastr.success(this.t.instant('pages.creator.kb.done', { title: item.title }), this.t.instant('pages.creator.kb.doneTitle'));
      } else if (outcome === 'failed') {
        this.toastr.danger(this.t.instant('pages.creator.kb.failed'), this.t.instant('pages.creator.kb.failedTitle'));
      } else {
        this.toastr.info(this.t.instant('pages.creator.kb.slow'), this.t.instant('pages.creator.kb.slowTitle'));
      }
      this.onChanged();
    } catch (err) {
      const failure = creatorFailure(err);
      const text = failure.code === 'bad_request'
        ? this.t.instant('pages.creator.kb.nothingToSave')
        : failure.message || this.t.instant('pages.creator.kb.failed');
      this.toastr.danger(text, this.t.instant('pages.creator.kb.failedTitle'));
    } finally {
      this.working = false;
    }
  }

  private async waitForJob(jobId: string): Promise<'done' | 'failed' | 'timeout'> {
    const deadline = Date.now() + 120_000;
    for (;;) {
      try {
        const state = await firstValueFrom(this.library.jobState(jobId));
        if (state === 'done') return 'done';
        if (state === 'failed') return 'failed';
      } catch {
        // a network hiccup or a fresh token on its way: ask again
      }
      if (Date.now() + 1500 > deadline) return 'timeout';
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
  }
}
